import {
  Card,
  Col,
  Input,
  message,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreateClientNew } from '../../components/client/create-client-new';
import { HWButton } from '../../components/shared/StyledButton';
import '../../styles/global.css';
import { CrmType, crmTypeMeta, getCrmDisplayName } from '../../types/crmTypes';
import { UserContext } from '../../UserContext';

const { Title } = Typography;
const { Option } = Select;

interface Client {
  id: string;
  friendlyName: string;
  clientState: string;
  isSingleAgent: boolean;
  clientId: number;
  crm_type?: string;
  hubspot_deal_id?: string;
  hubspot_company_id?: string;
}

const ClientsPage: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useContext(UserContext);
  const selectedClientState = searchParams.get('state') || 'all';
  const singleAgentFilter = searchParams.get('singleAgentFilter') || 'all';
  const [crmTypeFilter, setCrmTypeFilter] = useState('all');
  const [isCreateClientNewOpen, setIsCreateClientNewOpen] = useState(false);
  const navigate = useNavigate();

  const handleUnifiedSearch = async (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '') {
      setSearchText('');
      return;
    }
    // Check if it's already a numeric client ID that we have in our list
    const numericIdMatch = clients.find(c => c.clientId.toString() === trimmed);
    if (numericIdMatch) {
      setSearchText(trimmed);
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/find/${encodeURIComponent(trimmed)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );

      if (response.ok) {
        const result = (await response.json()) as
          | { type: 'agent'; clientId: string; agentId: string }
          | { type: 'lead'; clientId: string; leadId: string };
        setSearchText('');
        if (result.type === 'agent') {
          navigate(`/agentSms/${result.clientId}?agentId=${result.agentId}`);
        } else {
          navigate(
            `/lead-engagements/${result.clientId}?leadId=${result.leadId}`,
          );
        }
        return;
      }

      // No match — fall back to local client name search
      setSearchText(trimmed);
    } catch (error) {
      console.error('Find failed:', error);
      setSearchText(trimmed);
    }
  };

  const clientStates = useMemo(() => {
    const states = Array.from(
      new Set(clients.map(client => client.clientState)),
    );
    return ['all', ...states.sort()];
  }, [clients]);

  const filteredClients = useMemo(
    () =>
      clients
        .filter(client => {
          if (!searchText) {
            return true;
          }

          const searchLower = searchText.toLowerCase();

          const nameMatch = client.friendlyName
            .toLowerCase()
            .includes(searchLower);

          const idMatch =
            client.clientId !== undefined &&
            client.clientId.toString().includes(searchText);

          const hubspotIdMatch =
            (client.hubspot_deal_id &&
              client.hubspot_deal_id.toLowerCase().includes(searchLower)) ||
            (client.hubspot_company_id &&
              client.hubspot_company_id.toLowerCase().includes(searchLower));

          return nameMatch || idMatch || hubspotIdMatch;
        })
        .filter(client => {
          if (singleAgentFilter === 'exclude') {
            return !client.isSingleAgent;
          } else if (singleAgentFilter === 'only') {
            return client.isSingleAgent;
          }
          return true; // 'all'
        })
        .filter(client =>
          selectedClientState !== 'all'
            ? client.clientState === selectedClientState
            : true,
        )
        .filter(client =>
          crmTypeFilter !== 'all' ? client.crm_type === crmTypeFilter : true,
        ),
    [
      clients,
      singleAgentFilter,
      searchText,
      selectedClientState,
      crmTypeFilter,
    ],
  );

  const handleClientStateChange = (value: string) => {
    setSearchParams({ state: value, singleAgentFilter }); // Update URL params
  };

  const handleSingleAgentFilterChange = (value: string) => {
    setSearchParams({ state: selectedClientState, singleAgentFilter: value }); // Update URL params
  };

  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        if (user == null) {
          return;
        }
        const response = await fetch(`${apiUrl}/api/clients`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch clients');
        }
        const data = await response.json();

        setClients(data);
      } catch (error) {
        setClients([]);
        await message.error('Failed to load clients. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    void fetchClients();
  }, [apiUrl, user?.accessToken]);

  const handleRowClick = async (record: Client) => {
    if (
      record.clientState === 'created' ||
      record.clientState === 'verification'
    ) {
      navigate(`/client/${record.id}/verify-new`);
    } else if (record.clientState === 'backfill') {
      navigate(`/client/${record.id}/backfill-status`);
    } else if (record.clientState === 'backfill-failed') {
      await message.error('Backfill failed, reach out to engineering');
    } else {
      navigate(`/client/${record.clientId}/settings`);
    }
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <Card
        bordered={false}
        style={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginBottom: '24px',
        }}
        title={
          <Title level={2} style={{ margin: 0, fontSize: '28px' }}>
            Client Configuration
          </Title>
        }
      >
        <div style={{ marginBottom: 24, textAlign: 'left' }}>
          <Row align="middle" gutter={[16, 16]}>
            <Col lg={6} md={8} sm={10} xs={24}>
              <Input.Search
                allowClear
                onChange={e => setSearchText(e.target.value)}
                onSearch={value => void handleUnifiedSearch(value)}
                placeholder="Search by agent, client, or HubSpot ID"
                size="large"
                style={{ width: '100%' }}
                value={searchText}
              />
            </Col>

            <Col lg={4} md={6} sm={8} xs={24}>
              <Select
                onChange={handleClientStateChange}
                placeholder="Filter by state"
                size="large"
                style={{ width: '100%' }}
                value={selectedClientState}
              >
                {clientStates.map(state => (
                  <Option key={state} value={state}>
                    {state === 'all' ? 'All States' : state}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col lg={4} md={6} sm={8} xs={24}>
              <Select
                onChange={setCrmTypeFilter}
                placeholder="Filter by CRM Type"
                size="large"
                style={{ width: '100%' }}
                value={crmTypeFilter}
              >
                <Option key="all" value="all">
                  All CRM Types
                </Option>
                {Object.entries(crmTypeMeta).map(([type, meta]) => (
                  <Option key={type} value={type}>
                    {meta.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col lg={6} md={8} sm={10} xs={24}>
              <Segmented
                onChange={handleSingleAgentFilterChange}
                options={[
                  { label: 'Show All', value: 'all' },
                  { label: 'Hide Single Agents', value: 'exclude' },
                  { label: 'Only Single Agents', value: 'only' },
                ]}
                style={{
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #d3c2e8',
                  borderRadius: '8px',
                  padding: '4px',
                }}
                value={singleAgentFilter}
              />
            </Col>
          </Row>
        </div>
        <Table
          columns={[
            {
              title: 'Name',
              dataIndex: 'friendlyName',
              key: 'name',
              sorter: (a, b) => a.friendlyName.localeCompare(b.friendlyName),
            },
            {
              title: 'Client ID',
              dataIndex: 'clientId',
              key: 'clientId',
              sorter: (a, b) => a.clientId - b.clientId,
            },
            {
              title: 'Status',
              key: 'status',
              dataIndex: 'clientState',
              sorter: (a, b) => a.clientState.localeCompare(b.clientState),
            },
            {
              title: 'CRM Type',
              dataIndex: 'crm_type',
              key: 'crm_type',
              sorter: (a, b) =>
                (a.crm_type || '').localeCompare(b.crm_type || ''),
              render: (type: string) => getCrmDisplayName(type as CrmType),
            },
            {
              title: 'Is Single Agent',
              key: 'isSingleAgent',
              dataIndex: 'isSingleAgent',
              render: (text: boolean) => (text ? 'Yes' : 'No'),
              sorter: (a, b) =>
                Number(a.isSingleAgent) - Number(b.isSingleAgent),
            },
            {
              title: 'HubSpot ID',
              dataIndex: 'hubspot_company_id',
              key: 'hubspot_company_id',
              sorter: (a, b) =>
                (a.hubspot_company_id || '').localeCompare(
                  b.hubspot_company_id || '',
                ),
            },
          ]}
          dataSource={filteredClients}
          loading={loading}
          onRow={record => ({
            onClick: () => void handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          rowKey="id"
          style={{ marginBottom: '24px' }}
        />

        <Space size="middle">
          <HWButton
            onClick={() => setIsCreateClientNewOpen(true)}
            size="large"
            type="primary"
          >
            Create New Client
          </HWButton>
        </Space>
      </Card>
      <CreateClientNew
        apiUrl={apiUrl}
        isOpen={isCreateClientNewOpen}
        setIsOpen={setIsCreateClientNewOpen}
      />
    </div>
  );
};

export default ClientsPage;
